import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';

import { createFileRoute } from '@tanstack/react-router';
import { SaleLayout } from '~/components/common/sale/sale-layout';
import { useSale } from '~/components/common/sale/use-sale';
import { Illustration } from '~/components/ui/illustration/illustration';
import { Loader } from '~/components/ui/loader';
import { withAuthorizationRequired } from '~/libs/auth';
import { AppLoader } from '~/routes/_auth';
import { titleWrapper } from '~/utils/title';
import { saleQueryParamsSchema } from '~/validators/sale-validator';

export const SALE_SCOPES = [
  'scope.meusnidus.sales.view',
  'scope.meusnidus.sales.write',
];

export const Route = createFileRoute('/_auth/_main-navigation/vente/$saleId')({
  component: withAuthorizationRequired(RouteComponent, {
    onRedirecting: AppLoader,
    requireScope: SALE_SCOPES,
  }),
  validateSearch: (search) => saleQueryParamsSchema.parse(search),
});

function RouteComponent() {
  const { t } = useTranslation();
  const { saleId } = Route.useParams();
  const {
    data: sale,
    isLoading: saleIsLoading,
    error: saleError,
  } = useSale({ saleId });

  if (saleIsLoading) {
    return (
      <>
        <title>{titleWrapper(t('sale_page.loading'))}</title>
        <section className="relative">
          <div className="h-screen space-y-4 p-6 sm:p-14">
            <div className="flex items-center justify-center">
              <Loader className="mt-2 size-8" />
            </div>
          </div>
        </section>
      </>
    );
  }

  if (sale === null && !saleError) {
    return (
      <>
        <title>{titleWrapper(t('not_found.title'))}</title>
        <section className="relative">
          <div className="h-screen space-y-4 p-6 sm:p-14">
            <div className="flex h-full flex-col items-center justify-center">
              <Illustration illustration="boxEmpty" />
              <h3 className="mt-6 text-3.5xl font-bold">
                {t('sale_page.sale_not_found')}
              </h3>
            </div>
          </div>
        </section>
      </>
    );
  }

  const isCollection = Boolean(sale?.collectionSale);

  return (
    <>
      <title>
        {titleWrapper(sale?.brand?.name ?? sale?.selection_name ?? '')}
      </title>
      <DndProvider backend={HTML5Backend}>
        <SaleLayout isCollection={isCollection} />
      </DndProvider>
    </>
  );
}
